import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, GripVertical, Image, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";

const COLOR_PRESETS = ["#00d4ff", "#00e5a0", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#3b82f6"];

function SlideForm({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial || {
    headline: "", highlight: "", sub: "", cta: "Shop Now", link: "/category", accent: "#00d4ff", active: true, order: 0,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {[["headline", "Headline (e.g. Premium iPhones)"], ["highlight", "Highlight (e.g. Up to 40% OFF)"]].map(([k, ph]) => (
          <label key={k} style={{ fontSize: 12, color: "#8899b0" }}>{ph}
            <input value={form[k]} onChange={e => set(k, e.target.value)} required placeholder={ph} data-testid={`banner-${k}-input`}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }} />
          </label>
        ))}
        <label style={{ fontSize: 12, color: "#8899b0", gridColumn: "span 2" }}>Sub-text (optional)
          <input value={form.sub} onChange={e => set("sub", e.target.value)} placeholder="Short tagline below highlight" data-testid="banner-sub-input"
            style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }} />
        </label>
        {[["cta", "Button Text (e.g. Shop Now)"], ["link", "Link (e.g. /category or /sell)"]].map(([k, ph]) => (
          <label key={k} style={{ fontSize: 12, color: "#8899b0" }}>{ph}
            <input value={form[k]} onChange={e => set(k, e.target.value)} required placeholder={ph} data-testid={`banner-${k}-input`}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }} />
          </label>
        ))}
        <label style={{ fontSize: 12, color: "#8899b0" }}>Accent Color
          <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
            {COLOR_PRESETS.map(c => (
              <button key={c} type="button" onClick={() => set("accent", c)}
                style={{ width: 22, height: 22, borderRadius: "50%", background: c, border: form.accent === c ? "3px solid #fff" : "2px solid transparent", cursor: "pointer" }} />
            ))}
            <input type="color" value={form.accent} onChange={e => set("accent", e.target.value)}
              style={{ width: 30, height: 22, borderRadius: 4, border: "none", cursor: "pointer", background: "none" }} />
            <code style={{ fontSize: 11, color: "#6b7f9e" }}>{form.accent}</code>
          </div>
        </label>
        <label style={{ fontSize: 12, color: "#8899b0" }}>Order (lower = first)
          <input type="number" min="0" value={form.order} onChange={e => set("order", Number(e.target.value))} data-testid="banner-order-input"
            style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }} />
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#c0cfe0", cursor: "pointer" }}>
          <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} data-testid="banner-active-toggle" />
          Active (visible on homepage)
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button type="button" onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", border: "1px solid #3a5070", color: "#8899b0", cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button type="submit" disabled={busy} className="retry-pay-btn" data-testid="save-banner-button" style={{ padding: "8px 18px" }}>
            {busy ? <RefreshCw size={13} className="spin-slow" /> : null}
            {busy ? "Saving…" : "Save Slide"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function AdminBannerManager({ query = "" }) {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/homepage/banners");
      setSlides(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    setBusy(true);
    try {
      await api.post("/api/admin/homepage/banners", form);
      toast.success("Slide created!");
      setCreating(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const update = async (id, form) => {
    setBusy(true);
    try {
      await api.patch(`/api/admin/homepage/banners/${id}`, form);
      toast.success("Slide updated!");
      setEditId(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (slide) => {
    try {
      await api.patch(`/api/admin/homepage/banners/${slide.id}`, { active: !slide.active });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this banner slide?")) return;
    try {
      await api.delete(`/api/admin/homepage/banners/${id}`);
      toast.success("Slide deleted");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const visible = slides.filter(s => !query || s.headline?.toLowerCase().includes(query.toLowerCase()));

  return (
    <section className="admin-manager" data-testid="admin-banner-manager">
      <div className="admin-panel">
        <div className="panel-head">
          <div>
            <h2>Homepage Banner Slides</h2>
            <span>{slides.length} slides · {slides.filter(s => s.active).length} active</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="primary-btn" onClick={() => { setCreating(true); setEditId(null); }}
              style={{ padding: "6px 14px", fontSize: 13 }} data-testid="add-banner-button">
              <Plus size={14} style={{ marginRight: 4 }} /> Add Slide
            </button>
            <RefreshCw size={16} style={{ cursor: "pointer" }} onClick={load} />
          </div>
        </div>

        {/* Create form */}
        {creating && (
          <div style={{ marginBottom: 20, padding: 16, borderRadius: 12, border: "1px solid #00d4ff33", background: "#00d4ff06" }}>
            <h3 style={{ fontSize: 14, marginBottom: 12, color: "#00d4ff" }}>New Banner Slide</h3>
            <SlideForm onSave={create} onCancel={() => setCreating(false)} busy={busy} />
          </div>
        )}

        {loading ? <p className="admin-empty-copy">Loading…</p>
          : visible.length === 0 ? (
            <div className="empty-state" data-testid="no-banners-state">
              <Image size={28} />
              <h3>Koi banner slides nahi hain</h3>
              <p>Pehla slide add karo — homepage pe live dikhega</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {visible.map((s) => (
                <div key={s.id} data-testid={`banner-slide-${s.id}`}>
                  {editId === s.id ? (
                    <div style={{ padding: 14, borderRadius: 12, border: "1px solid #00d4ff33", background: "#00d4ff06" }}>
                      <SlideForm initial={s} onSave={form => update(s.id, form)} onCancel={() => setEditId(null)} busy={busy} />
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${s.active ? s.accent + "44" : "#1e3050"}`, background: "#0a1628" }}>
                      {/* Accent preview */}
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: s.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 16 }}>🎯</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <b style={{ fontSize: 14, color: "#e0e8f0" }}>{s.headline}</b>
                          <span style={{ fontSize: 12, fontWeight: 700, color: s.accent }}>{s.highlight}</span>
                          {s.active ? <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: "#22c55e22", color: "#22c55e" }}>LIVE</span>
                            : <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: "#6b7f9e22", color: "#6b7f9e" }}>HIDDEN</span>}
                        </div>
                        <small style={{ color: "#6b7f9e", fontSize: 11 }}>{s.sub || "No subtitle"} · CTA: "{s.cta}" → {s.link}</small>
                        <small style={{ color: "#4a6080", fontSize: 10, display: "block" }}>Order: {s.order} · Color: {s.accent}</small>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button onClick={() => toggleActive(s)} title={s.active ? "Hide" : "Show"}
                          style={{ padding: "5px 8px", borderRadius: 6, background: s.active ? "#22c55e11" : "#6b7f9e11", border: `1px solid ${s.active ? "#22c55e33" : "#3a5070"}`, color: s.active ? "#22c55e" : "#6b7f9e", cursor: "pointer" }}
                          data-testid={`toggle-banner-${s.id}`}>
                          {s.active ? <Eye size={13} /> : <EyeOff size={13} />}
                        </button>
                        <button onClick={() => { setEditId(s.id); setCreating(false); }}
                          style={{ padding: "5px 10px", borderRadius: 6, background: "#00d4ff11", border: "1px solid #00d4ff33", color: "#00d4ff", cursor: "pointer", fontSize: 11 }}
                          data-testid={`edit-banner-${s.id}`}>Edit</button>
                        <button onClick={() => del(s.id)}
                          style={{ padding: "5px 8px", borderRadius: 6, background: "#ef444411", border: "1px solid #ef444433", color: "#ef4444", cursor: "pointer" }}
                          data-testid={`delete-banner-${s.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </section>
  );
}
